/*  ===========================================================
    src/Components/SelectTodoTemplate.js
    -----------------------------------------------------------
    Release-specific To-Do table with:
      • custom “Done” checkbox  (gray → green)
      • border-free tidy inputs / selects
      • task re-ordering ▲▼ buttons
      • pencil / trash / arrows tinted #565656
      • collapsed-category memory
      • hide / show completed-tasks toggle
      • switching selector back to “None” fully clears template ⬅ NEW
=========================================================== */

import React, { useEffect, useState } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { api } from '../api';

import pencilIcon from '../assets/icons/pencil.svg';
import trashIcon  from '../assets/icons/trash.svg';
import checkIcon  from '../assets/icons/checkcircle.svg'; // (kept in case you use it elsewhere)

/* ─────────────────────────────────────────────────────────── */
/* Shared styles                                              */
const tidyField = {
  backgroundColor: '#2c2c2c',
  color:           '#fff',
  border:          '0px solid #555',
  borderRadius:    '6px',
  fontFamily:      'inherit',
  padding:         '8px',
  outline:         'none'
};

const grayIconFilter =
  'invert(33%) sepia(1%) saturate(0%) hue-rotate(163deg) brightness(97%) contrast(89%)';

/* ─────────────────────────────────────────────────────────── */

export default function SelectTodoTemplate({ releaseId, selectedTemplateId }) {
  /* ─── state ─── */
  const [release, setRelease]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error,   setError]             = useState(null);

  const [editingTask, setEditingTask]   = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [showDelete,   setShowDelete]   = useState(false);

  const [currentTab, setCurrentTab]       = useState('Operations');
  const [showCompleted, setShowCompleted] = useState(false);

  /* remembered collapsed categories */
  const [collapsedCats, setCollapsedCats] = useState(()=>{
    try { return JSON.parse(localStorage.getItem(`collapsedCats_${releaseId}`)) || {}; }
    catch { return {}; }
  });
  const toggleCollapse = id=>{
    const next = {...collapsedCats, [id]: !collapsedCats[id]};
    setCollapsedCats(next);
    localStorage.setItem(`collapsedCats_${releaseId}`, JSON.stringify(next));
  };

  const departments = ['Operations','Marketing','Socials+Creative','A&R'];

  /* ───────────────────────────────────────────────────────── */
  /* 1. Fetch release                                         */
  /* ───────────────────────────────────────────────────────── */
  useEffect(()=>{
    if(!releaseId){ setLoading(false); return; }
    (async ()=>{
      try{
        setLoading(true);
        const r = await api(`/api/releases/${releaseId}`);
        if(!r.ok) throw new Error(r.status);
        setRelease(await r.json());
      }catch(e){ setError(e.message); }
      finally{ setLoading(false); }
    })();
  },[releaseId]);

  /* ───────────────────────────────────────────────────────── */
  /* 2. When template ID changes (clone)                      */
  /* ───────────────────────────────────────────────────────── */
  useEffect(()=>{
    if(!release || !selectedTemplateId) return;

    const currentId = typeof release.templateId==='object'
      ? release.templateId?._id
      : release.templateId;
    if(currentId === selectedTemplateId) return;

    (async ()=>{
      try{
        const tpl = await api(`/api/todotemplates/${selectedTemplateId}`).then(r=>r.json());

        const map={};
        const newCats  = (tpl.categories||[]).map(c=>{
          const id=crypto.randomUUID(); map[c._id]=id;
          return {_id:id,name:c.name,department:c.department};
        });

        const newTasks = (tpl.tasks||[]).map(t=>{
          const id=crypto.randomUUID(); map[t._id]=id;
          return {
            _id:id,
            categoryId: map[t.categoryId] || t.categoryId,
            name:t.name,
            linkUrl: t.linkUrl || '',
            daysBefore:t.daysBefore||'',
            status:'Not Started',
            completedOn:null,
            dependencyTaskId: t.dependencyTaskId ? map[t.dependencyTaskId] : null
          };
        });

        await saveRelease({
          ...release,
          templateId:selectedTemplateId,
          releaseCategories:newCats,
          releaseTasks:newTasks
        });
      }catch(e){ alert('Could not clone template'); }
    })();
  },[selectedTemplateId, release]);  // clone-effect deps

  /* ───────────────────────────────────────────────────────── */
  /* 3. If user selects “None” → clear template & tasks  ⬅ NEW */
  /* ───────────────────────────────────────────────────────── */
  useEffect(()=>{
    if(!release)                return;
    if(selectedTemplateId)      return;                 // they didn’t pick None
    if(!release.templateId)     return;                 // already clear
    if((release.releaseTasks||[]).length===0 &&
       (release.releaseCategories||[]).length===0) return;

    const cleared = {
      ...release,
      templateId:        null,
      releaseCategories: [],
      releaseTasks:      []
    };

    (async ()=>{
      try{ await saveRelease(cleared); }
      catch(e){ alert('Could not clear template'); }
    })();
  },[selectedTemplateId, release]);  // clear-effect deps

  /* ─── save helper ─── */
  const saveRelease = async doc=>{
    const r = await api(`/api/releases/${doc._id}`,{
      method:'PUT',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify(doc)
    });
    if(!r.ok) throw new Error(r.status);
    setRelease(await r.json());
  };

  /* ─── early states ─── */
  if(loading) return <p style={{color:'#999'}}>Loading…</p>;
  if(error)   return <p style={{color:'salmon'}}>Error: {error}</p>;
  if(!release) return null;

  const cats  = release.releaseCategories || [];
  const tasks = release.releaseTasks      || [];

  /* ───────────────────────────────────────────────────────── */
  /* CRUD helpers                                             */
  /* ───────────────────────────────────────────────────────── */
  const addTask = catId=>{
const n={
  _id: crypto.randomUUID(),
  categoryId: catId,
  name: '',
  linkUrl: '',                                  // ⬅ NEW
  daysBefore: '',
  status: 'Not Started',
  completedOn: null,
  dependencyTaskId: null
};    saveRelease({...release,releaseTasks:[...tasks,n]});
    setEditingTask(n);
  };

  const moveTask = (id,delta)=>{
    const arr=[...tasks];
    const i=arr.findIndex(t=>t._id===id); if(i<0) return;
    const j=i+delta; if(j<0||j>=arr.length) return;
    arr.splice(j,0,arr.splice(i,1)[0]);
    saveRelease({...release,releaseTasks:arr});
  };

  const isOverdue = t=>{
    if(!release.releaseDate||t.status==='Completed'||!t.daysBefore) return false;
    const d=new Date(release.releaseDate); d.setDate(d.getDate()-Number(t.daysBefore));
    return d < new Date();
  };
  const dueDateStr  = t=>{
    if(!release.releaseDate||!t.daysBefore) return '-';
    const d=new Date(release.releaseDate); d.setDate(d.getDate()-Number(t.daysBefore));
    return d.toLocaleDateString('en-GB');
  };
  const compDateStr = d=> d ? new Date(d).toLocaleDateString('en-GB') : '-';

  const toggleTaskStatus = (task,isEdit=false)=>{
    const makeCompleted = !(isEdit ? editingTask.status==='Completed'
                                   : task.status       ==='Completed');
    const newStatus = makeCompleted ? 'Completed' : 'Not Started';

    if(isEdit){
      setEditingTask({...editingTask,status:newStatus,
        completedOn:makeCompleted ? new Date().toISOString() : null});
    }else{
      const upd = tasks.map(t=>t._id===task._id
        ? {...t,status:newStatus,completedOn:makeCompleted ? new Date().toISOString() : null}
        : t);
      saveRelease({...release,releaseTasks:upd});
    }
  };

  /* ───────────────────────────────────────────────────────── */
  /* Small UI pieces                                          */
  /* ───────────────────────────────────────────────────────── */
  const Checkbox = ({checked,onChange})=>(
    <label style={{cursor:'pointer'}}>
      <input type="checkbox" checked={checked} onChange={onChange} style={{display:'none'}}/>
      <span style={{
        display:'inline-block',width:24,height:24,border:'2px solid',
        borderColor:checked?'#7eef4f':'#555',
        background: checked?'#7eef4f':'transparent',
        borderRadius:4,position:'relative',transition:'all .15s'}}>
        {checked && (
          <svg viewBox="0 0 24 24"
               style={{position:'absolute',top:-2,left:-2,width:26,height:26,stroke:'#1e1e1e',strokeWidth:3,fill:'none'}}>
            <path d="M5 13l4 4L19 7"/>
          </svg>
        )}
      </span>
    </label>
  );

  const DependencyDropdown = ({task})=>{
    const field={...tidyField,padding:'6px 8px',appearance:'none'};

    const dept   = task.depDepartment   || '';
    const catId  = task.depCategoryId   || '';
    const taskId = task.dependencyTaskId|| '';

    const catsInDept = cats.filter(c=>c.department===dept);
    const tasksInCat = tasks.filter(t=>t.categoryId===catId);

    return (
      <div style={{display:'flex',flexDirection:'column',gap:5}}>
        <select style={field} value={dept}
          onChange={e=>setEditingTask(p=>({...p,depDepartment:e.target.value,depCategoryId:null,dependencyTaskId:null}))}>
          <option value="">No Dept</option>{departments.map(d=><option key={d}>{d}</option>)}
        </select>
        <select style={field} disabled={!dept} value={catId}
          onChange={e=>setEditingTask(p=>({...p,depCategoryId:e.target.value,dependencyTaskId:null}))}>
          <option value="">No Category</option>{catsInDept.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        <select style={field} disabled={!catId} value={taskId}
          onChange={e=>setEditingTask(p=>({...p,dependencyTaskId:e.target.value}))}>
          <option value="">No Task</option>
          {tasksInCat.filter(t=>t._id!==task._id).map(t=><option key={t._id} value={t._id}>{t.name}</option>)}
        </select>
      </div>
    );
  };

  /* ───────────────────────────────────────────────────────── */
  /* Category block                                           */
  /* ───────────────────────────────────────────────────────── */
  const CategoryBlock = cat=>{
    const collapsed   = !!collapsedCats[cat._id];
    const allCatTasks = tasks.filter(t=>t.categoryId===cat._id);
    const catTasks    = allCatTasks.filter(t=> showCompleted || t.status!=='Completed');

    const pct     = allCatTasks.length
      ? allCatTasks.filter(t=>t.status==='Completed').length / allCatTasks.length * 100
      : 0;
    const overdue = allCatTasks.some(isOverdue);

    return (
      <div key={cat._id} style={{marginBottom:20,padding:20,background:'#1e1e1e',borderRadius:10}}>
        {/* header */}
        <div onClick={()=>toggleCollapse(cat._id)}
             style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',marginBottom:10}}>
          <span style={{color:'#aaa',fontSize:12}}>{collapsed?'▶':'▼'}</span>
          <h3 style={{margin:0,color:overdue?'#ff4c4c':'#fff'}}>{cat.name}{overdue?' ⚠':''}</h3>
          <div style={{width:150,height:10,background:'#333',borderRadius:5,overflow:'hidden'}}>
            <div style={{width:`${pct}%`,height:'100%',background:'#7eef4f'}}/>
          </div>
        </div>

        {!collapsed && (
          <>
            {/* table */}
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:'#333',color:'#fff'}}>
                  <th style={{padding:10,textAlign:'left'}}>Task</th>
                  <th style={{padding:10,textAlign:'left'}}>Days</th>
                  <th style={{padding:10,textAlign:'left'}}>Due</th>
                  <th style={{padding:10,textAlign:'left'}}>Done</th>
                  <th style={{padding:10,textAlign:'left'}}>Completed</th>
                  <th style={{padding:10,textAlign:'left'}}>Dependency</th>
                  <th style={{padding:10,textAlign:'left'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {catTasks.length===0 && (
                  <tr><td colSpan={7} style={{padding:10}}>No tasks.</td></tr>
                )}
                {catTasks.map((t,idx)=>{
                  const isEdit = editingTask && editingTask._id===t._id;

                  const dep = t.dependencyTaskId
                    ? tasks.find(x=>x._id===t.dependencyTaskId)
                    : null;
                  const depLabel = dep
                    ? (()=>{const c=cats.find(k=>k._id===dep.categoryId);
                            return c?`${c.department} - ${c.name} - ${dep.name}`:dep.name; })()
                    : 'None';

                  return (
                    <tr key={t._id} style={{borderBottom:'1px solid #555',background:'#1e1e1e'}}>
                      {/* name (+ optional link) */}
<td style={{padding:10}}>
  {isEdit ? (
    <div style={{ display:'grid', gap:6 }}>
      <input
        value={editingTask.name}
        onChange={e=>setEditingTask(p=>({...p,name:e.target.value}))}
        placeholder="Task name"
        style={{...tidyField,width:'100%'}}
      />
      <input
        type="url"
        value={editingTask.linkUrl || ''}
        onChange={e=>setEditingTask(p=>({...p,linkUrl:e.target.value}))}
        placeholder="https://link-for-this-task.example"
        style={{...tidyField,width:'100%'}}
      />
    </div>
  ) : (
    t.linkUrl ? (
      <a
        href={t.linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color:'#fff', textDecoration:'underline' }}
        onClick={(e)=>e.stopPropagation()}
        title={t.linkUrl}
      >
        {t.name || t.linkUrl}
      </a>
    ) : (
      t.name
    )
  )}
</td>

                      {/* daysBefore */}
                      <td style={{padding:10}}>
                        {isEdit
                          ? <input type="number" value={editingTask.daysBefore||''}
                                   onChange={e=>setEditingTask(p=>({...p,daysBefore:e.target.value}))}
                                   style={{...tidyField,width:70,textAlign:'center'}}/>
                          : t.daysBefore||'-'}
                      </td>

                      {/* due */}
                      <td style={{padding:10,color:isOverdue(t)?'#ff4c4c':'#fff'}}>{dueDateStr(t)}</td>

                      {/* checkbox */}
                      <td style={{padding:10}}>
                        <Checkbox
                          checked={isEdit?editingTask.status==='Completed':t.status==='Completed'}
                          onChange={()=>toggleTaskStatus(t,isEdit)}
                        />
                      </td>

                      {/* completedOn */}
                      <td style={{padding:10}}>{compDateStr(t.completedOn)}</td>

                      {/* dependency */}
                      <td style={{padding:10}}>
                        {isEdit
                          ? <DependencyDropdown task={editingTask}/>
                          : depLabel}
                      </td>

                      {/* actions */}
                      <td style={{padding:10,whiteSpace:'nowrap'}}>
                        {isEdit ? (
                          <>
                            <button
                              onClick={()=>{
                                const upd = tasks.map(x=>x._id===editingTask._id ? editingTask : x);
                                saveRelease({...release,releaseTasks:upd});
                                setEditingTask(null);
                              }}
                              style={{background:'#28a745',border:'none',borderRadius:5,padding:'5px 10px',color:'#fff',marginRight:5}}>
                              Save
                            </button>
                            <button
                              onClick={()=>setEditingTask(null)}
                              style={{background:'#d9534f',border:'none',borderRadius:5,padding:'5px 10px',color:'#fff'}}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            {/* reorder */}
                            <button
                              onClick={()=>moveTask(t._id,-1)}
                              disabled={idx===0}
                              style={{
                                background:'transparent',border:'none',
                                cursor:idx===0?'default':'pointer',
                                opacity:idx===0?0.25:1,color:'#565656',
                                fontSize:16,lineHeight:'16px',marginRight:4}}>
                              ▲
                            </button>
                            <button
                              onClick={()=>moveTask(t._id,1)}
                              disabled={idx===catTasks.length-1}
                              style={{
                                background:'transparent',border:'none',
                                cursor:idx===catTasks.length-1?'default':'pointer',
                                opacity:idx===catTasks.length-1?0.25:1,color:'#565656',
                                fontSize:16,lineHeight:'16px',marginRight:10}}>
                              ▼
                            </button>

                            {/* edit / delete */}
                            <button
                              onClick={()=>setEditingTask(t)}
                              style={{background:'transparent',border:'none',cursor:'pointer',marginRight:5}}>
                              <img src={pencilIcon} alt="" style={{width:20,height:20,filter:grayIconFilter}}/>
                            </button>
                            <button
                              onClick={()=>{setTaskToDelete(t);setShowDelete(true);}}
                              style={{background:'transparent',border:'none',cursor:'pointer'}}>
                              <img src={trashIcon} alt="" style={{width:20,height:20,filter:grayIconFilter}}/>
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <button
              onClick={()=>addTask(cat._id)}
              style={{marginTop:10,padding:'10px 20px',background:'#555',color:'#fff',
                      border:'none',borderRadius:5,cursor:'pointer'}}>
              Add Task
            </button>
          </>
        )}
      </div>
    );
  };

  /* ───────────────────────────────────────────────────────── */
  /* Department tabs                                          */
  /* ───────────────────────────────────────────────────────── */
  const DepartmentTabs = ()=>{
    const existing = departments.filter(d=>cats.some(c=>c.department===d));
    return (
      <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
        {existing.map(dep=>{
          const depCats = cats.filter(c=>c.department===dep);
          const depTasks= tasks.filter(t=>depCats.some(c=>c._id===t.categoryId));
          const pct     = depTasks.length
            ? depTasks.filter(t=>t.status==='Completed').length / depTasks.length * 100
            : 0;
          const overdue = depTasks.some(isOverdue);
          return (
            <div key={dep} onClick={()=>setCurrentTab(dep)}
              style={{
                display:'flex',alignItems:'center',justifyContent:'space-between',
                width:200,padding:'10px 15px',
                background:currentTab===dep?'#555':'#181818',
                borderRadius:10,cursor:'pointer',
                color:overdue?'#ff4c4c':'#fff',fontWeight:'bold'
              }}>
              <span>{dep}{overdue?' ⚠':''}</span>
              <div style={{width:30,height:30}}>
                <CircularProgressbar
                  value={pct}
                  styles={buildStyles({pathColor:'#7eef4f',trailColor:'#333',strokeLinecap:'butt'})}/>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /* ───────────────────────────────────────────────────────── */
  /* Render                                                   */
  /* ───────────────────────────────────────────────────────── */
  return (
    <div style={{color:'#fff',marginTop:20}}>

      {/* Tabs (left) + toggle (right) */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <DepartmentTabs/>

        <button
          onClick={()=>setShowCompleted(v=>!v)}
          style={{padding:'8px 14px',background:'#444',color:'#fff',
                  border:'none',borderRadius:6,cursor:'pointer'}}>
          {showCompleted ? 'Hide Completed Tasks' : 'Show Completed Tasks'}
        </button>
      </div>

      {/* categories */}
      {cats.filter(c=>c.department===currentTab).map(CategoryBlock)}

      {/* delete confirm */}
      {showDelete && taskToDelete && (
        <div className="modal">
          <div className="modal-content"
            style={{width:400,padding:20,background:'#1e1e1e',borderRadius:10,margin:'auto',color:'#fff'}}>
            <h2>Confirm Delete</h2>
            <p>Delete task “{taskToDelete.name}”?</p>
            <div style={{display:'flex',justifyContent:'space-between'}}>
              <button
                onClick={()=>{
                  saveRelease({...release,releaseTasks:tasks.filter(x=>x._id!==taskToDelete._id)});
                  setShowDelete(false);
                }}
                style={{background:'#d9534f',border:'none',borderRadius:5,padding:'10px 20px',color:'#fff'}}>
                Delete
              </button>
              <button
                onClick={()=>setShowDelete(false)}
                style={{background:'#555',border:'none',borderRadius:5,padding:'10px 20px',color:'#fff'}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}